// src/components/reveal/Reveal.jsx для анімації
import React from "react";
import useInView from "../hooks/useInView";

/**
 * Reveal — обгортка, що додає клас "is-revealed" при вході у в'юпорт.
 *
 * props:
 * - as: HTML тег або React-компонент (за замовч.: 'div')
 * - variant: 'up' | 'down' | 'left' | 'right' | 'fade' | 'zoom' (за замовч.: 'up')
 * - delay: число в секундах (0.0, 0.06, ...)
 * - duration: число в секундах (за замовч.: 0.55)
 * - once: true/false — одноразово чи при кожному вході (за замовч.: true)
 * - threshold, root, rootMargin: параметри IntersectionObserver
 * - className: додаткові класи
 */
export default function Reveal({
  as: Tag = "div",
  variant = "up",
  delay = 0,
  duration = 0.55,
  once = true,
  threshold = 0.15,
  root = null,
  rootMargin = "0px",
  className = "",
  children,
  style,
  ...rest
}) {
  const { ref, inView } = useInView({ threshold, root, rootMargin, once });

  return (
    <Tag
      ref={ref}
      className={`reveal reveal--${variant} ${inView ? "is-revealed" : ""} ${className}`}
      style={{
        "--reveal-delay": `${delay}s`,
        "--reveal-duration": `${duration}s`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * RevealGroup — зручний каскад для набору дочірніх елементів.
 * props:
 * - as, variant, baseDelay, step, duration, once, ... — дефолти для дітей
 */
export function RevealGroup({
  as: Wrapper = "div",
  children,
  variant = "up",
  baseDelay = 0,
  step = 0.06,
  duration = 0.5,
  once = true,
  className = "",
  ...rest
}) {
  const kids = React.Children.toArray(children);
  return (
    <Wrapper className={className} {...rest}>
      {kids.map((child, i) => (
        <Reveal
          key={i}
          variant={variant}
          delay={baseDelay + i * step}
          duration={duration}
          once={once}
        >
          {child}
        </Reveal>
      ))}
    </Wrapper>
  );
}
